import React from 'react';
import { motion } from 'framer-motion';
import {
  ShoppingBag, DollarSign, Trophy, Package,
  Heart, Factory, Plane, GraduationCap,
  ArrowRight, Sparkles
} from 'lucide-react';
import '../styles/Industries.css';

const Industries = () => {
  const industries = [
    {
      id: 'retail',
      icon: '🛍️',
      lucideIcon: <ShoppingBag />,
      title: 'Retail & E-Commerce',
      subtitle: 'Transforming Customer Experiences',
      description:
        'Drive measurable growth with AI-powered personalization, pricing intelligence, and end-to-end demand planning — from discovery to fulfilment.',
      topics: ['Personalization', 'Dynamic Pricing', 'Inventory & Demand', 'Search & Discovery', 'Customer Support'],
      caseStudy: { metric: '35%', detail: 'increase in conversion rates', description: 'Through AI-driven personalization' },
      image: '/images/industries/retail.jpg'
    },
    {
      id: 'finance',
      icon: '💰',
      lucideIcon: <DollarSign />,
      title: 'Finance & Banking',
      subtitle: 'Intelligent Financial Services',
      description:
        'Reduce risk and improve customer experience with real-time fraud detection, credit intelligence, and automation across core operations.',
      topics: ['Fraud Detection', 'Credit Risk', 'Compliance', 'Process Automation', 'Customer Ops'],
      caseStudy: { metric: '60%', detail: 'reduction in fraud losses', description: 'Using real-time AI detection' },
      image: '/images/industries/finance.jpg'
    },
    {
      id: 'sports',
      icon: '⚡',
      lucideIcon: <Trophy />,
      title: 'Sports & Entertainment',
      subtitle: 'Elevating Fan Engagement',
      description:
        'Turn data into unforgettable experiences — personalize content, optimize pricing, and deliver immersive, digital-first fan journeys.',
      topics: ['Fan Engagement', 'Content Personalization', 'Dynamic Pricing', 'Performance Analytics', 'AR Experiences'],
      caseStudy: { metric: '45%', detail: 'boost in fan engagement', description: 'With AI-powered content' },
      image: '/images/industries/sports.jpg'
    },
    {
      id: 'cpg',
      icon: '📦',
      lucideIcon: <Package />,
      title: 'CPG',
      subtitle: 'Smart Distribution & Marketing',
      description:
        'Forecast demand, optimize spend, and improve availability with data-driven planning across production, distribution, and retail execution.',
      topics: ['Demand Forecasting', 'Marketing Mix', 'Availability', 'Supply Chain', 'Sustainability'],
      caseStudy: { metric: '25%', detail: 'reduction in waste', description: 'Through predictive analytics' },
      image: '/images/industries/cpg.jpg'
    },
    {
      id: 'healthcare',
      icon: '🏥',
      lucideIcon: <Heart />,
      title: 'Healthcare & Life Sciences',
      subtitle: 'Advancing Patient Care',
      description:
        'Accelerate diagnostics and decision support with responsible AI — improving quality of care while scaling operations safely.',
      topics: ['Imaging AI', 'Clinical Support', 'Patient Risk', 'Operations', 'Research Acceleration'],
      caseStudy: { metric: '40%', detail: 'faster diagnosis', description: 'With AI-assisted imaging' },
      image: '/images/industries/healthcare.jpg'
    },
    {
      id: 'manufacturing',
      icon: '🏭',
      lucideIcon: <Factory />,
      title: 'Industrial & Manufacturing',
      subtitle: 'Industry 4.0 Transformation',
      description:
        'Build resilient operations with predictive maintenance, quality automation, and real-time production optimization.',
      topics: ['Predictive Maintenance', 'Quality Automation', 'Production Optimization', 'Digital Twins', 'Energy'],
      caseStudy: { metric: '50%', detail: 'reduction in downtime', description: 'Using predictive maintenance' },
      image: '/images/industries/manufacturing.jpg'
    },
    {
      id: 'travel',
      icon: '✈️',
      lucideIcon: <Plane />,
      title: 'Travel & Hospitality',
      subtitle: 'Personalizing Journeys',
      description:
        'Increase revenue per guest with smart pricing, personalization, and automation — from booking to on-property experiences.',
      topics: ['Revenue Management', 'Personalization', 'Demand Forecasting', 'Ops Automation', 'Guest Experience'],
      caseStudy: { metric: '20%', detail: 'increase in revenue per guest', description: 'Through AI personalization' },
      image: '/images/industries/travel.jpg'
    },
    {
      id: 'education',
      icon: '🎓',
      lucideIcon: <GraduationCap />,
      title: 'Education & EdTech',
      subtitle: 'Personalized Learning at Scale',
      description:
        'Improve learning outcomes with adaptive systems, intelligent content, and educator copilots designed for real classrooms.',
      topics: ['Adaptive Learning', 'Assessment', 'Personalized Content', 'Virtual Tutoring', 'Curriculum Insights'],
      caseStudy: { metric: '55%', detail: 'improvement in learning outcomes', description: 'With adaptive AI systems' },
      image: '/images/industries/education.jpg'
    }
  ];

  return (
    <div className="industries-page">
      {/* HERO */}
      <section className="industries-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="industries-hero-content"
          >


            <h1 className="industries-hero-title">
              <span className="title-accent"> AI Transformation across Industries</span>
            </h1>


          </motion.div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="industries-showcase">
        <div className="container-wide">
          <div className="industries-list">
            {industries.map((industry, index) => (
              <motion.article
                key={industry.id}
                id={industry.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.55, delay: Math.min(index * 0.04, 0.2) }}
                className={`industry-feature ${index % 2 === 1 ? 'reverse' : ''}`}
              >
                <div className="industry-copy">
                  <div className="industry-kicker">
                    <span className="industry-emoji">{industry.icon}</span>
                    <span className="industry-subtitle-text">{industry.subtitle}</span>
                  </div>

                  <h2 className="industry-title-lg">{industry.title}</h2>
                  <p className="industry-description-lg">{industry.description}</p>

                  <div className="industry-topics" aria-label={`${industry.title} topics`}>
                    {industry.topics.map((t) => (
                      <span className="topic-chip" key={t}>
                        {t}
                      </span>
                    ))}
                  </div>




                </div>

                <div
                  className="industry-media"
                  style={{
                    backgroundImage: industry.image ? `url(${industry.image})` : undefined
                  }}
                  role="img"
                  aria-label={`${industry.title} cover`}
                >
                  <div className="media-overlay">
                    <div className="media-icon" aria-hidden="true">{industry.lucideIcon}</div>
                    <div className="media-tag">{industry.subtitle}</div>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="industries-cta-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="industries-cta-content"
          >
            <h2>Want a tailored industry roadmap?</h2>
            <p>We’ll map the highest-ROI AI use cases for your context and show you a clear execution plan.</p>
            <a href="/contact" className="btn btn-primary btn-lg">
              Schedule Consultation
              <ArrowRight size={20} />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Industries;